/**
 * Re-fetches a Gov Promotion package from the interim workspace endpoints (CT-5095).
 *
 * TEMPORARY: uses `/v1/workspace/promotion/*` until the unified My Work promotion
 * API ships (CT-4867). After a failed attestation batch, Work Queues calls this so
 * the detail panel reflects which claims GS already marked reviewed/removed.
 */

import {
  getPromotionDetails,
  listPromotionClaims,
} from '../../../services/promotionActions.js';
import {
  countConflictClaims,
  mapPromotionClaimsToUi,
} from './mapPromotionClaims.js';
import { buildGovPromotionSituation } from './mapPromotionToEvent.js';

/**
 * Loads the latest promotion details + candidate claims from GS.
 * @param {string} promotionUuid
 * @returns {Promise<{ claims: Object[], claimCount: number, conflictCount: number, situation: string }|null>}
 */
export async function fetchPromotionDetailSnapshot(promotionUuid) {
  if (!promotionUuid) return null;

  const [details, claimsResult] = await Promise.all([
    getPromotionDetails(promotionUuid),
    listPromotionClaims(promotionUuid, { pageSize: 100 }),
  ]);

  const rawClaims = claimsResult?.items ?? [];
  const claims = mapPromotionClaimsToUi(rawClaims);
  const conflictCount = countConflictClaims(rawClaims);
  const metadata = details?.metadata ?? {};
  const claimCount = metadata.claimCount ?? details?.claimCounts?.total ?? claims.length;
  const situation = buildGovPromotionSituation(
    claimCount,
    conflictCount,
    metadata.description ?? null,
  );

  return { claims, claimCount, conflictCount, situation };
}

/**
 * Merges a fresh GS snapshot into an in-memory Gov Promotion event.
 * @param {Object} event
 * @param {Object} snapshot - output of {@link fetchPromotionDetailSnapshot}
 * @returns {Object}
 */
export function mergePromotionDetailSnapshot(event, snapshot) {
  if (!event || !snapshot) return event;
  return {
    ...event,
    situation: snapshot.situation ?? event.situation,
    description: snapshot.situation ?? event.description,
    detail: {
      ...event.detail,
      claims: snapshot.claims,
      claimCount: snapshot.claimCount,
      conflictCount: snapshot.conflictCount,
    },
  };
}
